import { Dialog, FormInput, FormSectionHeader, FormSelect, Heading, Loader } from "@/components";
import useAddEdit from "./useAddEdit";
import { Button, Card, CardContent, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Label, RadioGroup, RadioGroupItem, Switch } from "@/components/ui";
import { Dot, House, MapPin, MoreVertical, Network, Pen, Sparkles, Star, Trash2, Users } from "lucide-react";
import { FormProvider } from "react-hook-form";
import { ADDRESS_1, ADDRESS_2, CITY, COMMUNITY, CORRESPONDENCE_ADDRESS, CUSTOM_ADDRESS, EMAIL_ID_1, EMAIL_ID_2, EXISTING, FIRST_NAME, JOINT, LAST_NAME, MOBILE_NO, MULTI_CORRESPOND, OCCUPANCY_TYPE, ORGANIZATION_NAME, OWNERSHIP_SHARE, OWNERSHIP_TYPE, PO_BOX, PRIMARY_USER, PROPERTY, STATE, USERS, USER_TYPE, ZIPCODE, addressCorrespondOptions, orgOwnershipType, ownershipTypeOptions} from "./consts";
import { capitalize, cn } from "@/lib/utils";
import { AddEditDialog } from "./AddEditDialog";
import { RemoveJointOwnerDialog } from "./RemoveJointOwnerDialog";
import moment from "moment";

const AddEdit = () => {
  const { breadcrumbData, form, isCommunityLoading, communityList, title, isOwnershipDtlLoading,
    propertyList, isPropertyLoading, onOwnershipTypeChange, formData, ownerCount, communityData,
    onAddUser, onSave, isMobile, onEditUser, selectedUser, action, onSubmit, onChangePrimary, isView,
    openAddEditDialog, setOpenAddEditDialog, onAddressTypeChange, isFromMasterData,
    propertyName, ownershipShare, primaryContact, isUpdateOwnershipPending, isAddOwnershipPending, isEdit,
    primaryUserAddress, primaryUserDtl, openRemoveOwnerDialog, setOpenRemoveOwnerDialog, onRemoveOwner,
    onRemoveSave, isStateLoading, stateList, keys, onDeleteConfirmIndividualUser,
    onDeleteIndividualUser, openDeleteSingleOwnerDialog, setOpenDeleteSingleOwnerDialog, isLoadingConsentDtl,
    consentDtl, neighborList, isEnabledConsent
   } = useAddEdit();
   const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row lg:items-center w-full justify-between">
        <Heading
          title={`${title} - ${communityData?.name}`}
          breadcrumbData={breadcrumbData}
        />
      </div>

      {isCommunityLoading || isOwnershipDtlLoading || isStateLoading ? <Loader /> :
        <FormProvider {...form}>
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="grid grid-cols-1 lg:grid-cols-6 mt-4 gap-[20px]">
              <div className="lg:col-span-4 grid grid-cols-1 gap-[20px]">
                <Card className="shadow-none rounded-sm p-0 border-gray-200">
                  <CardContent className="p-4">
                    <FormSectionHeader className="flex flex-row items-center gap-2">
                      <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                        <Network className="stroke-white h-4 w-4" />
                      </div>
                      Attach To Property
                    </FormSectionHeader>

                    <div className="grid gird-cols-1 md:grid-cols-2 gap-[20px]">
                      <FormSelect
                        name={COMMUNITY}
                        label="Community"
                        placeholder="Please Select"
                        options={communityList?.map((d: any) => ({
                          value: d.communityId,
                          label: d.name,
                        }))}
                        disabled
                      />
                      <FormSelect
                        name={PROPERTY}
                        label="Property"
                        placeholder="Please Select"
                        options={propertyList?.map((d: any) => ({
                          value: d.propertyId?.toString(),
                          label: d.name,
                        }))}
                        disabled={isPropertyLoading || isView || !isFromMasterData}
                      />
                    </div>
                  </CardContent>
                </Card>

                {formData[PROPERTY] ? <>
                  <Card className="shadow-none rounded-sm p-0 border-gray-200">
                    <CardContent className="p-4">
                      <FormSectionHeader className="flex flex-row items-center gap-2">
                        <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                          <House className="stroke-white h-4 w-4" />
                        </div>
                        <div className="flex flex-col">
                          <span>Ownership Type</span>
                          <span className="text-gray-500 text-[12px] font-normal">Defines the title on this property; drives correspondence & how many owners are required.</span>
                        </div>
                      </FormSectionHeader>

                      <div className="flex flex-col md:flex-row gap-3">
                        {ownershipTypeOptions?.map((data: any) => {
                          const Icon = data.icon;
                          return (
                            <Button variant="outline" type="button"
                              className={cn(formData[OWNERSHIP_TYPE] == data.value ? "text-white bg-primary hover:text-white hover:bg-primary" : "text-gray-500")}
                              onClick={onOwnershipTypeChange(data.value)} disabled={isView}>
                              <Icon className="h-5 w-5" />
                              {data?.label}
                            </Button>
                          )
                        })}
                      </div>

                      <div className="bg-blue-50 rounded-sm flex flex-row items-center gap-3 text-primary text-[12px] p-3 mt-4">
                        <div className="h-3 w-3 bg-primary rounded-full" />
                        <span>Individual title allows <span className="font-semibold">one Owner only.</span> Additional people can be added as tenants or other type.</span>
                      </div>
                    </CardContent>
                  </Card>

                  {orgOwnershipType.includes((formData as any)[OWNERSHIP_TYPE]) ? <Card>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2">
                      <FormInput
                        name={`${ORGANIZATION_NAME}`}
                        control={form.control}
                        label="Organization Name"
                        placeholder="Please enter"
                        error={(errors as any)[`${ORGANIZATION_NAME}`]?.message}
                        isRequired
                      />
                    </CardContent>
                  </Card> : <></>}

                  <Card className="shadow-none rounded-sm p-0 border-gray-200">
                    <CardContent className="p-4">
                      <FormSectionHeader className="flex flex-col md:flex-row md:items-center gap-2 justify-between w-full">
                        <div className="flex flex-row items-center gap-2">
                          <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                            <Users className="stroke-white h-4 w-4" />
                          </div>
                          <div className="flex flex-col">
                            <span>Users on this title <span className="text-gray-400 text-[12px]">({ownerCount} Owner)</span></span>
                          </div>
                        </div>
                        <Button type="button" onClick={onAddUser} disabled={isView}>+ Add User</Button>
                      </FormSectionHeader>

                      {(formData as any)[USERS]?.length ?
                        <div className="flex flex-col gap-3">
                          {(formData as any)[USERS]?.map((data: any, idx: number) => {
                            if (data.delete) return;
                            const isOwner = data[OCCUPANCY_TYPE] == "OWNER";
                            return (
                              <div className="grid grid-cols-1 md:grid-cols-6 border border-gray-300 px-2 py-3 rounded-sm">
                                <div className="flex flex-row gap-2 items-center md:col-span-4">
                                  {!isMobile && <div className="bg-gray-300 flex items-center justify-center h-[25px] w-[25px] rounded-sm mr-3">{idx+1}</div>}
                                  <div className="bg-primary flex items-center justify-center rounded-full h-[30px] w-[30px] md:h-[50px] md:w-[50px]">
                                    <span className="text-white text-[15px]">{data[FIRST_NAME]?.charAt(0)?.toUpperCase()}{data[LAST_NAME]?.charAt(0)?.toUpperCase()}</span>
                                  </div>
                                  <div className="flex flex-col">
                                    <div className="flex flex-col md:flex-row md:items-end gap-1">
                                      <span className="font-medium">{data[FIRST_NAME]} {data[LAST_NAME]}</span>
                                      <div className="flex flex-row items-center gap-1">
                                        <span className="text-[10px] bg-blue-100 text-primary px-2 py-1 rounded-sm font-medium">{data[USER_TYPE] == EXISTING ? "Existing User" : "New User"}</span>
                                        {data[PRIMARY_USER] ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-sm font-medium">Primary</span> : <></>}
                                      </div>
                                    </div>
                                    <div className="text-gray-400 text-[12px] flex flex-col md:flex-row md:items-center">
                                      <span>{data[EMAIL_ID_1]}</span>
                                      {!isMobile && <Dot className="h-3 w-3"/>}
                                      <span>{data[MOBILE_NO]}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-row gap-2 items-center md:justify-end mt-2 md:mt-auto self-end md:col-span-2">
                                  <div className="flex flex-row bg-gray-100 gap-2 items-center p-2 rounded-sm">
                                    <Label className="text-[10px] text-gray-400">Type: </Label>
                                    <span className="text-[12px] ">{capitalize(data[OCCUPANCY_TYPE])}</span>
                                  </div>
                                  {isOwner ? <div className="flex flex-row bg-gray-100 gap-2 items-center p-2 rounded-sm">
                                    <Label className="text-[10px] text-gray-400">Share(%): </Label>
                                    <span className="text-[12px] ">{data[OWNERSHIP_SHARE] || 0}</span>
                                  </div> : <></>}
                                  {isView ? <></> : <DropdownMenu>
                                    <DropdownMenuTrigger>
                                      <MoreVertical className="w-5 h-7 cursor-pointer" />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align="end"
                                      className="bg-white border-0 shadow-md"
                                    >
                                      <DropdownMenuItem
                                        className="flex items-center gap-2"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          onEditUser(data, idx);
                                        }}
                                      >
                                        <Pen className="w-4 h-4" />
                                        Edit
                                      </DropdownMenuItem>
                                      {isOwner ? <DropdownMenuItem
                                        className="flex items-center gap-2"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          onChangePrimary(idx);
                                        }}
                                      >
                                        <Star className={cn("w-4 h-4", data[PRIMARY_USER] ? "fill-primary" : "")} />
                                        Make Primary Contact
                                      </DropdownMenuItem> : <></>}
                                      {(formData as any)[OWNERSHIP_TYPE] == JOINT ? <DropdownMenuItem
                                      className="flex items-center gap-2"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onRemoveOwner(data);
                                      }}
                                    >
                                      <Trash2 className={cn("w-4 h-4")} />
                                      Delete
                                    </DropdownMenuItem> : <DropdownMenuItem
                                      className="flex items-center gap-2"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onDeleteIndividualUser(data);
                                      }}
                                    >
                                      <Trash2 className={cn("w-4 h-4")} />
                                      Delete
                                    </DropdownMenuItem>}
                                    </DropdownMenuContent>
                                  </DropdownMenu>}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      : <div className="border border-gray-300 rounded-sm flex items-center justify-center flex flex-col py-5 gap-5">
                        <span className="text-[16px] text-center font-semibold">No user are linked to this property yet.</span>
                        <span className="text-[14px] text-gray-400 text-center">Add an existing owner from the user Master, <br/>
                        or create a new owner and link them to this property.</span>
                      </div>}
                    </CardContent>
                  </Card>
                  <Card className="shadow-none rounded-sm p-0 border-gray-200">
                    <CardContent className="p-4">
                      <FormSectionHeader className="flex flex-row items-center gap-2 justify-between w-full">
                        <div className="flex flex-row items-center gap-2">
                          {/* <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                            <MapPin className="stroke-white h-4 w-4" />
                          </div> */}
                          <div className="flex flex-col">
                            <span>Address For Correspondence</span>
                          </div>
                        </div>
                      </FormSectionHeader>

                      <div className="bg-blue-50 rounded-sm flex flex-row items-center gap-3 text-primary text-[12px] p-3 mt-4">
                        <div className="h-[15px] w-[20px] bg-primary rounded-full" />
                        <span>Editable by the Primary Contact only - every change to the correspondence address is written to the audit trail (who/ when / old - new).</span>
                      </div>

                      <span className="text-[12px] text-gray-500">Choose one source (single point of truth — the app maps to the live record, so there's no copy drift):</span>

                      <div className="my-4">
                        <RadioGroup
                          onValueChange={onAddressTypeChange}
                          value={(formData as any)[CORRESPONDENCE_ADDRESS]}
                          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                          disabled={isView}
                        >
                          {addressCorrespondOptions?.map((data: any, index: number) => {
                            const isSelected = (formData as any)[CORRESPONDENCE_ADDRESS] == data?.value;
                            return (
                              <div className={cn("border-2 border-gray-300 rounded-sm p-4", isSelected ? "border-primary" : "")}>
                                <div className="flex flex-row items-start gap-2">
                                  <RadioGroupItem
                                    value={data?.value}
                                    id={data?.value}
                                    className={cn(isSelected ? "border-primary" : "", "cursor-pointer")}
                                  />
                                  <Label htmlFor={data?.value} className="font-medium text-[16px] leading-5 cursor-pointer">{index+1}. {data?.title}</Label>
                                </div>
                                <Label className="my-4 font-thin text-[12px]">{data?.text1}</Label>
                                <Label className="text-green-600 font-thin text-[12px]">{data?.text2}</Label>
                              </div>
                            )
                          })}
                        </RadioGroup>
                      </div>

                      {/* <div>
                        <FormCheckbox
                          name={`${MULTI_CORRESPOND}`}
                          control={form.control}
                          label={<span className="text-black text-[12px]"><span className="font-semibold">Multi-correspondence</span> — email → all opted-in owners; physical mail lists all names.</span>}
                          error={(errors as any)[`${MULTI_CORRESPOND}`]?.message}
                          isRequired={requiredFields.includes(`${MULTI_CORRESPOND}`)}
                          disabled={isView}
                        />
                      </div> */}
                    </CardContent>
                  </Card>
                  {formData[CORRESPONDENCE_ADDRESS] == "custom" ?
                    <Card className="shadow-none rounded-sm p-0 border-gray-200">
                      <CardContent className="p-4">
                        <FormSectionHeader className="flex flex-row items-center gap-2 justify-between w-full">
                          <div className="flex flex-row items-center gap-2">
                            <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                              <MapPin className="stroke-white h-4 w-4" />
                            </div>
                            <div className="flex flex-col">
                              <span>Custom Address</span>
                            </div>
                          </div>
                        </FormSectionHeader>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                          <FormInput
                            name={`${CUSTOM_ADDRESS}.${ADDRESS_1}`}
                            control={form.control}
                            label="Address 1"
                            placeholder="Please enter"
                            error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${ADDRESS_1}`]?.message}
                            isRequired
                          />
                          <FormInput
                            name={`${CUSTOM_ADDRESS}.${ADDRESS_2}`}
                            control={form.control}
                            label="Address 2"
                            placeholder="Please enter"
                            error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${ADDRESS_2}`]?.message}
                          />
                          <FormSelect
                            key={keys.state}
                            name={`${CUSTOM_ADDRESS}.${STATE}`}
                            control={form.control}
                            label="State"
                            placeholder="Please select"
                            error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${STATE}`]?.message}
                            isRequired
                            options={stateList?.map((data: any) => ({
                              label: data.name,
                              value: data.code,
                            }))}
                          />
                          <FormInput
                            name={`${CUSTOM_ADDRESS}.${CITY}`}
                            control={form.control}
                            label="City"
                            placeholder="Please select"
                            error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${CITY}`]?.message}
                            isRequired
                            variant="char"
                          />
                          <FormInput
                            name={`${CUSTOM_ADDRESS}.${ZIPCODE}`}
                            control={form.control}
                            label="Zipcode"
                            placeholder="Please enter"
                            error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${ZIPCODE}`]?.message}
                            isRequired
                            maxLength={10}
                            variant="integerSpeChar"
                          />
                          <FormInput
                            name={`${CUSTOM_ADDRESS}.${PO_BOX}`}
                            control={form.control}
                            label="PO Box"
                            placeholder="Please enter"
                            error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${PO_BOX}`]?.message}
                            isRequired
                            minLength={3}
                            maxLength={3}
                            variant="integer"
                          />
                        </div>
                      </CardContent>
                    </Card> 
                  : <></>}
                </> : <></>}
              </div>
              <div className="lg:col-span-2 grid grid-cols-1 gap-[20px]">
                <Card className="shadow-none rounded-sm p-0 border-gray-200">
                  <CardContent className="p-4">
                    <FormSectionHeader className="flex flex-row items-center gap-2">
                      <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                        <Sparkles className="stroke-white h-4 w-4" />
                      </div>
                      Summary
                    </FormSectionHeader>
                    <div className="border border-gray-300 rounded-sm text-[14px] p-2 flex items-center justify-between">
                      <div className="flex flex-col gap-2 text-gray-600">
                        <span>Property</span>
                        <span>Community</span>
                        <span>Title Type</span>
                        <span>Owners on Title</span>
                        <span>Total Ownership</span>
                        <span>Primary Contact</span>
                      </div>
                      <div className="flex flex-col gap-2 text-right font-medium">
                        <span>{propertyName || " "}</span>
                        <span>{communityData?.name}</span>
                        <span>{capitalize((formData as any)?.ownershipType)}</span>
                        <span>{ownerCount} Owner</span>
                        <span>{ownershipShare}</span>
                        <span>{primaryContact}</span>
                      </div>
                    </div>
                    {isView ? <></> : 
                      <div className="flex items-center justify-center w-full">
                        <Button type="button" className="mt-4 w-full"
                          onClick={form.handleSubmit(onSubmit)}>
                          {isUpdateOwnershipPending || isAddOwnershipPending ? 
                          <Loader variant="secondary" /> : <></>}
                          {isEdit ? "Update" : "Create"} Ownership & Link
                        </Button>
                      </div>
                    }
                  </CardContent>
                </Card>
                <Card className="shadow-none rounded-sm p-0 border-gray-200">
                  <CardContent className="p-4">
                    <FormSectionHeader className="flex flex-row items-center gap-2">
                      <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                        <Sparkles className="stroke-white h-4 w-4" />
                      </div>
                      Correspondence Preview
                    </FormSectionHeader>

                    <div className="text-[14px]">
                      <span>Physical mail - Multi correspondence {formData[MULTI_CORRESPOND] ? "On" : "Off"}</span>

                      <div className="border border-gray-300 my-3 p-3 rounded-sm flex flex-col gap-2">
                        {primaryContact || primaryUserAddress ? 
                          <>
                            <span className="font-semibold">{primaryContact}</span>
                            <span>{primaryUserAddress}</span>
                          </> : <span className="text-[10px] font-medium">No user data found</span>}
                      </div>
                      <div className="flex flex-col gap-2">
                        <span>Email Fan Out</span>
                        <span>{(primaryUserDtl as any)[EMAIL_ID_1]}</span>
                        <span>{(primaryUserDtl as any)[EMAIL_ID_2]}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-none rounded-sm p-0 border-gray-200">
                  <CardContent className="p-4">
                    <FormSectionHeader className="flex flex-row gap-2">
                      <div className="flex items-center justify-center h-8 w-8 p-2 bg-primary rounded-full">
                        <Users className="stroke-white" />
                      </div>
                      <div className="flex flex-col">
                        <span>Contact Content Sharing</span>
                        <span className="text-[12px] text-gray-400 font-normal">View and manage this owner's contact sharing preference</span>
                      </div>
                    </FormSectionHeader>
                    {isEdit || isView ? isLoadingConsentDtl ? <Loader /> :
                      <div className="border border-gray-200 rounded-md grid grid-cols-1 text-[12px] p-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div className="flex flex-col">
                            <span className="text-[12px] text-gray-500">Consent Status</span>
                            <div className="flex flex-row items-center gap-2">
                              <Switch checked={consentDtl?.consentEnabled}  disabled className="data-[state=checked]:bg-green-600" />
                              <span className={isEnabledConsent ? "text-green-600 font-semibold" : ""}>{isEnabledConsent ? "Enabled" : "Disabled"}</span>
                            </div>
                          </div>
                          {isEnabledConsent ? <div className="flex flex-col">
                            <span className="text-[12px] text-gray-500">Active Since</span>
                            <span>{consentDtl?.consentDate ? moment(consentDtl?.consentDate).local().format("MM/DD/YYYY HH:mm A") : ""}</span>
                          </div> : <></>}
                          {isEnabledConsent ? <div className="flex flex-col">
                            <span className="text-[12px] text-gray-500">Consent Provided By</span>
                            <span>{consentDtl?.consentSource || ""}</span>
                          </div> : <></>}
                          <div className="flex flex-col">
                            <span className="text-[12px] text-gray-500">Last Updated By</span>
                            <span>{consentDtl?.updatedByName || ""}</span>
                          </div>
                          {isEnabledConsent ? <div className="flex flex-col">
                            <span className="text-[12px] text-gray-500">On</span>
                            <span>{consentDtl?.updatedAt ? moment(consentDtl?.updatedAt).local().format("MM/DD/YYYY HH:mm A") : ""}</span>
                          </div> : <></>}
                        </div>
                        {
                          isEnabledConsent ? 
                            <>
                              <div className="flex flex-col gap-2 pt-2 border-t border-gray-200 mt-2">
                                <span>This owner is sharing their primary name, email and mobile number with selected neighboring properties.</span>
                                <div className="flex flex-col gap-2">
                                  {
                                    neighborList?.map((data: any) => {
                                      return (
                                        <div className="border border-gray-200 rounded-md p-2 flex flex-row gap-3 items-center"> 
                                          {`${data?.neighborPropertyName || ""} | ${data?.neighborBlockName || ""} | ${data?.neighborZoneName || ""} | Unit ${data?.neighborUnitNumber || ""}`} 
                                          <div className="text-green-600 bg-green-50 border border-green-600 py-1 px-2 rounded-sm">
                                            {capitalize(data?.status)}
                                          </div>
                                        </div>
                                      )
                                    })
                                  }
                                </div>
                              </div>
                              <div className="border border-blue-200 bg-blue-50 rounded-md p-2 mt-2"> 
                                One-directional sharing: This owner will see contact information of others only if they have also chosen to share with this property.
                              </div>
                            </> : <></>
                        }
                      </div> : <></>
                    }
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        </FormProvider>
      }

      {openAddEditDialog && 
        <AddEditDialog
          open={openAddEditDialog}
          setOpen={setOpenAddEditDialog}
          onSave={onSave}
          data={selectedUser}
          isEdit={action == "edit"}
          ownershipType={formData[OWNERSHIP_TYPE] as string}
          ownerCount={ownerCount}
        />
      }

      {
        openRemoveOwnerDialog && 
         <RemoveJointOwnerDialog
          open={openRemoveOwnerDialog}
          setOpen={setOpenRemoveOwnerDialog}
          selectedUser={selectedUser}
          allUsers={formData[USERS]}
          onSave={onRemoveSave}
        />
      }

      {
        openDeleteSingleOwnerDialog && 
        <Dialog
          open={openDeleteSingleOwnerDialog}
          setOpen={setOpenDeleteSingleOwnerDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Confirmation"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          saveText="Delete"
          saveBtnClassName="bg-red-500 text-white hover:bg-red-500 hover:text-white"
          onSave={onDeleteConfirmIndividualUser}
        >
          <p className="text-sm">
            Are you sure you want to delete the user -{" "}
            <span className="font-bold text-primary">
              {(selectedUser as any)?.[FIRST_NAME]} {(selectedUser as any)?.[LAST_NAME]}
            </span>
            ?
          </p>
        </Dialog>
      }
    </div>
  )
}

export default AddEdit;
